import * as THREE from 'three'
import CANNON from 'cannon'

export default class InformationSection
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.materials = _options.materials
        this.objects = _options.objects
        this.physics = _options.physics
        this.shadows = _options.shadows
        this.areas = _options.areas
        this.tiles = _options.tiles
        this.debug = _options.debug
        this.x = _options.x
        this.y = _options.y

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        this.setStatic()
        this.setLinks()
        this.setActivities()
        this.setFlag()
        this.setBurjKhalifa()
        this.setTiles()
    }

    setStatic()
    {
        const baseScene = this.resources.items.informationStaticBase.scene
        // Remove ONLY French elements (flag + Eiffel Tower + trees + pin)
        const removeNames = [
            'shadeGreen.002', 'shadeGreen.011', 'shadeGreen.014',  // Trees
            'shadeRed.001',    // Red map pin
            'shadeGray.007',   // Eiffel Tower structure
            'shadeWhite.111', 'shadeWhite', 'shadeWhite.112',  // Eiffel Tower white parts
            'shadeRed.005',    // French flag red
            'shadeBlue',       // French flag blue
            'shadeWhite.114',  // French flag white
        ]
        const toRemove = []
        baseScene.traverse((child) => {
            if(child.name && removeNames.includes(child.name))
            {
                toRemove.push(child)
            }
        })
        toRemove.forEach((child) => {
            if(child.parent) child.parent.remove(child)
        })

        this.objects.add({
            base: baseScene,
            collision: this.resources.items.informationStaticCollision.scene,
            floorShadowTexture: this.resources.items.informationStaticFloorShadowTexture,
            offset: new THREE.Vector3(this.x, this.y, 0),
            mass: 0
        })
    }

    setLinks()
    {
        // Set up
        this.links = {}
        this.links.x = 3.1
        this.links.y = - 1.5
        this.links.halfExtents = {}
        this.links.halfExtents.x = 1
        this.links.halfExtents.y = 1
        this.links.distanceBetween = 3
        this.links.labelWidth = this.links.halfExtents.x * 2 + 1
        this.links.labelGeometry = new THREE.PlaneGeometry(this.links.labelWidth, this.links.labelWidth * 0.25, 1, 1)
        this.links.labelOffset = - 1.6
        this.links.items = []

        this.links.container = new THREE.Object3D()
        this.links.container.matrixAutoUpdate = false
        this.container.add(this.links.container)

        // Options
        this.links.options = [
            {
                href: 'https://github.com/intruderfr',
                labelTexture: this.resources.items.informationContactGithubLabelTexture
            },
            {
                href: 'https://www.linkedin.com/in/aslam-ahamed',
                labelTexture: this.resources.items.informationContactLinkedinLabelTexture
            },
            {
                href: 'mailto:aslam@prestigeone.ae',
                labelTexture: this.resources.items.informationContactMailLabelTexture
            }
        ]

        // Create each link
        let i = 0
        for(const _option of this.links.options)
        {
            // Set up
            const item = {}
            item.x = this.x + this.links.x + this.links.distanceBetween * i
            item.y = this.y + this.links.y
            item.href = _option.href

            // Create area
            item.area = this.areas.add({
                position: new THREE.Vector2(item.x, item.y),
                halfExtents: new THREE.Vector2(this.links.halfExtents.x, this.links.halfExtents.y)
            })
            item.area.on('interact', () =>
            {
                window.open(_option.href, '_blank')
            })

            // Texture
            item.texture = _option.labelTexture
            item.texture.magFilter = THREE.NearestFilter
            item.texture.minFilter = THREE.LinearFilter

            // Create label
            item.labelMesh = new THREE.Mesh(this.links.labelGeometry, new THREE.MeshBasicMaterial({ wireframe: false, color: 0xffffff, alphaMap: _option.labelTexture, depthTest: true, depthWrite: false, transparent: true }))
            item.labelMesh.position.x = item.x + this.links.labelWidth * 0.5 - this.links.halfExtents.x
            item.labelMesh.position.y = item.y + this.links.labelOffset
            item.labelMesh.matrixAutoUpdate = false
            item.labelMesh.updateMatrix()
            this.links.container.add(item.labelMesh)

            // Save
            this.links.items.push(item)

            i++
        }
    }

    setActivities()
    {
        // Set up
        this.activities = {}
        this.activities.x = this.x + 0
        this.activities.y = this.y - 10
        this.activities.multiplier = 5.5

        // Geometry
        this.activities.geometry = new THREE.PlaneGeometry(2 * this.activities.multiplier, 1 * this.activities.multiplier, 1, 1)

        // Texture
        this.activities.texture = this.resources.items.informationActivitiesTexture
        this.activities.texture.magFilter = THREE.NearestFilter
        this.activities.texture.minFilter = THREE.LinearFilter

        // Material
        this.activities.material = new THREE.MeshBasicMaterial({ wireframe: false, color: 0xffffff, alphaMap: this.activities.texture, transparent: true })

        // Mesh
        this.activities.mesh = new THREE.Mesh(this.activities.geometry, this.activities.material)
        this.activities.mesh.position.x = this.activities.x
        this.activities.mesh.position.y = this.activities.y
        this.activities.mesh.matrixAutoUpdate = false
        this.activities.mesh.updateMatrix()
        this.container.add(this.activities.mesh)
    }

    setFlag()
    {
        this.flag = {}
        this.flag.size = { x: 4.4, y: 2.64 }
        this.flag.start = new THREE.Vector3(this.x + 10.6, this.y - 3.2, 2.8)

        this.flag.texture = this.resources.items.informationUAEFlagTexture
        this.flag.texture.colorSpace = THREE.SRGBColorSpace
        this.flag.texture.anisotropy = 4
        this.flag.texture.needsUpdate = true

        this.flag.geometry = new THREE.PlaneGeometry(this.flag.size.x, this.flag.size.y)
        this.flag.material = new THREE.MeshBasicMaterial({
            map: this.flag.texture,
            alphaMap: this.flag.texture,
            transparent: true,
            side: THREE.DoubleSide
        })

        this.flag.mesh = new THREE.Mesh(this.flag.geometry, this.flag.material)
        this.flag.mesh.position.copy(this.flag.start)
        this.container.add(this.flag.mesh)

        this.flag.body = new CANNON.Body({
            mass: 1.1,
            material: this.physics.materials.items.dummy,
            position: new CANNON.Vec3(this.flag.start.x, this.flag.start.y, this.flag.start.z)
        })
        this.flag.body.allowSleep = true
        this.flag.body.linearDamping = 0.35
        this.flag.body.angularDamping = 0.85
        this.flag.body.addShape(new CANNON.Box(new CANNON.Vec3(this.flag.size.x * 0.5, 0.1, this.flag.size.y * 0.5)))
        this.flag.body.quaternion.setFromEuler(Math.PI * 0.5, 0, 0, 'XYZ')
        this.physics.world.addBody(this.flag.body)

        this.time.on('tick', () =>
        {
            this.flag.mesh.position.set(this.flag.body.position.x, this.flag.body.position.y, this.flag.body.position.z)
            this.flag.mesh.quaternion.set(this.flag.body.quaternion.x, this.flag.body.quaternion.y, this.flag.body.quaternion.z, this.flag.body.quaternion.w)
        })
    }

    setBurjKhalifa()
    {
        this.burjKhalifa = {}
        this.burjKhalifa.position = new THREE.Vector3(this.x + 5.4, this.y + 2.2, 0)
        this.burjKhalifa.material = this.materials.shades.items.gold
        this.burjKhalifa.container = new THREE.Object3D()
        this.burjKhalifa.container.position.copy(this.burjKhalifa.position)

        const segments = [
            { width: 0.9, depth: 0.9, height: 2.5, z: 1.25 },
            { width: 0.7, depth: 0.7, height: 2.0, z: 3.5 },
            { width: 0.52, depth: 0.52, height: 1.6, z: 5.3 },
            { width: 0.38, depth: 0.38, height: 1.2, z: 6.7 },
            { width: 0.24, depth: 0.24, height: 1.0, z: 7.8 }
        ]

        for(const segment of segments)
        {
            const mesh = new THREE.Mesh(
                new THREE.BoxGeometry(segment.width, segment.depth, segment.height),
                this.burjKhalifa.material
            )
            mesh.position.z = segment.z
            this.burjKhalifa.container.add(mesh)
        }

        const spire = new THREE.Mesh(
            new THREE.ConeGeometry(0.1, 2.0, 6),
            this.burjKhalifa.material
        )
        spire.position.z = 9.3
        this.burjKhalifa.container.add(spire)

        this.container.add(this.burjKhalifa.container)

        this.shadows.add(this.burjKhalifa.container, {
            sizeX: 3.5,
            sizeY: 3.5,
            offsetZ: - 0.2,
            alpha: 0.25
        })

        this.burjKhalifa.body = new CANNON.Body({
            mass: 0,
            material: this.physics.materials.items.floor,
            position: new CANNON.Vec3(this.burjKhalifa.position.x, this.burjKhalifa.position.y, 4.5)
        })
        this.burjKhalifa.body.addShape(new CANNON.Box(new CANNON.Vec3(0.6, 0.6, 4.8)))
        this.physics.world.addBody(this.burjKhalifa.body)
    }

    setTiles()
    {
        this.tiles.add({
            start: new THREE.Vector2(this.x - 1.2, this.y + 13),
            delta: new THREE.Vector2(0, - 20)
        })
    }
}
